/**
 * BriefDrilldown — the written organization brief, as a page.
 *
 * Was the drawer behind KnowOrgCard. Server contract
 * (/classic-dashboard/org-brief): headline + themes + shifts + oneThing, where
 * all NUMBERS are server-computed and only the prose comes from the writer;
 * `ai:false` marks the deterministic fallback. Coverage is always stated.
 *
 * Person and project chips are links into the drill-down pages — this page is
 * the reader, those are the microscope.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Button as AntButton, Tooltip } from "antd";
import { ArrowLeft, RefreshCw, Sparkles } from "lucide-react";
import { PageShell } from "../../components/PageShell";
import { Breadcrumb } from "../../components/Breadcrumb";
import { Button } from "../../components/primitives";
import { DashAntd } from "../../components/DashAntd";
import { useDashTheme } from "../../hooks/useDashTheme";
import { orgBrief } from "../../components/drilldown/drilldownApi";
import { fmtMins } from "../../components/classic/micro";
import {
  useClassicFilters, classicPath, personPath, projectPath,
} from "../../components/drilldown/drilldownRoutes";
import { Crumbs } from "./shared";
import { useDemoMode, DemoBanner, DemoHint, demoBrief } from "./demoData";

const CSS = `
.tg-orgbrief .tgob-skel{border-radius:6px;background:linear-gradient(90deg,var(--tgob-a) 25%,var(--tgob-b) 37%,var(--tgob-a) 63%);
  background-size:400% 100%;animation:tgob-sh 1.2s infinite}
@keyframes tgob-sh{0%{background-position:100% 0}100%{background-position:0 0}}
.tg-orgbrief .tgob-in{opacity:0;transform:translateY(8px);animation:tgob-in .5s cubic-bezier(.2,.7,.2,1) forwards}
@keyframes tgob-in{to{opacity:1;transform:none}}
`;

const TONE = {
  up: { glyph: "▲", bg: "#e7f5ec", fg: "#2f9e63" },
  risk: { glyph: "●", bg: "#fdebec", fg: "#e5484d" },
  note: { glyph: "◆", bg: "#fff6e0", fg: "#b98a00" },
};

function Skeleton({ t }) {
  const widths = ["90%", "72%", "96%", "84%", "96%", "60%"];
  return (
    <div style={{ "--tgob-a": t.c.surfaceMuted, "--tgob-b": t.c.border }}>
      {widths.map((w, i) => (
        <div key={i} className="tgob-skel" style={{ width: w, height: i === 2 || i === 4 ? 56 : 12, margin: "14px 0" }} />
      ))}
    </div>
  );
}

function SectionLabel({ t, children, delay }) {
  return (
    <div className="tgob-in" style={{
      animationDelay: delay, fontSize: 10.5, fontWeight: 700, letterSpacing: ".6px",
      textTransform: "uppercase", color: t.c.textTertiary, margin: "20px 0 8px",
    }}>{children}</div>
  );
}

function Chip({ t, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        fontSize: 10.5, fontWeight: 600, color: t.c.primaryHover, background: t.c.primarySoft,
        border: "none", borderRadius: 999, padding: "4px 10px", cursor: "pointer", fontFamily: t.font.family,
      }}
    >{children}</button>
  );
}

export function BriefDrilldown() {
  const t = useDashTheme();
  const navigate = useNavigate();
  const { range, member } = useClassicFilters();

  const demo = useDemoMode();
  const [raw, setRaw] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [refreshTick, setRefreshTick] = React.useState(0);

  const startKey = range?.start ? new Date(range.start).toDateString() : null;
  const endKey = range?.end ? new Date(range.end).toDateString() : null;

  React.useEffect(() => {
    if (!startKey || !endKey) return undefined;
    const ac = new AbortController();
    setLoading(true); setError(null);
    orgBrief(range, { signal: ac.signal, member, refresh: refreshTick > 0 })
      .then((d) => setRaw(d))
      .catch((e) => { if (e?.code !== "ERR_CANCELED" && e?.message !== "canceled") setError(e.message); })
      .finally(() => setLoading(false));
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startKey, endKey, member, refreshTick]);

  /* Sample data FILLS a gap, never replaces a brief the server wrote. */
  const data = React.useMemo(
    () => ((demo && !loading && (!raw || raw.empty || !raw.headline)) ? demoBrief(range) : raw),
    [demo, loading, raw, range]
  );
  const showingSample = !!data?.__demo;
  const rangeLabel = data?.range ? `${data.range.start} – ${data.range.end}` : "";

  return (
    <PageShell
      title="Your organization, this period"
      subtitle={`${rangeLabel ? `${rangeLabel} · ` : ""}scoped to what you can see`}
      icon={<Sparkles size={16} />}
      actions={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Tooltip title="Write it again with the latest data">
            <AntButton
              size="small" type="text" icon={<RefreshCw size={14} />}
              style={{ color: t.c.textTertiary }}
              disabled={loading}
              onClick={() => setRefreshTick((n) => n + 1)}
            />
          </Tooltip>
          <Button
            variant="secondary" size="sm" leftIcon={<ArrowLeft size={14} />}
            onClick={() => navigate(classicPath(range, member))}
          >
            Dashboard
          </Button>
        </div>
      }
    >
      <DashAntd>
        <style>{CSS}</style>
        <Crumbs>
          <Breadcrumb items={[
            { label: "Dashboard", to: classicPath(range, member) },
            { label: "Know your organization" },
          ]} />
        </Crumbs>
        {showingSample && <DemoBanner t={t} what="This brief" />}

        <div className="tg-orgbrief" style={{ maxWidth: 760, fontFamily: t.font.family }}>
          {data && !loading && !error && data.coverage && (
            <div style={{
              fontSize: 11.5, color: t.c.textSecondary, background: t.c.primarySoft,
              borderRadius: 8, padding: "8px 12px", marginBottom: 4,
            }}>
              Grounded in analysed activity from <b>{data.coverage.analysedPeople} of {data.coverage.total} people</b> — nothing below is invented; every figure comes from tracked data.
            </div>
          )}

          {loading ? (
            <Skeleton t={t} />
          ) : error ? (
            <Alert type="error" showIcon style={{ marginTop: 14 }} message="The brief could not be written" description={error} />
          ) : !data || data.empty || !data.headline ? (
            <Alert
              type="info" showIcon style={{ marginTop: 14 }}
              message="Nothing to brief on"
              description={<>No tracked activity in this window for the people you can see.<br /><DemoHint t={t} /></>}
            />
          ) : (
            <>
              <p
                className="tgob-in"
                style={{ animationDelay: ".05s", fontSize: 15, lineHeight: 1.55, fontWeight: 500, margin: "16px 0 4px", color: t.c.text }}
                // Server-sanitised: the writer may only emit <b>/<em>; every other tag is stripped there.
                dangerouslySetInnerHTML={{
                  __html: String(data.headline)
                    .replace(/<em>/g, `<em style="font-style:normal;color:${t.status.negative};font-weight:650">`)
                    .replace(/<b>/g, `<b style="color:${t.c.text}">`),
                }}
              />

              {data.themes?.length > 0 && (
                <>
                  <SectionLabel t={t} delay=".15s">What the team actually worked on</SectionLabel>
                  {data.themes.map((th, i) => {
                    const pair = t.accentFor ? t.accentFor(String(th.project || "")) : null;
                    return (
                      <div key={`${th.project}-${i}`} className="tgob-in" style={{
                        animationDelay: `${0.2 + i * 0.08}s`, background: t.c.surfaceMuted,
                        borderRadius: 10, padding: "12px 14px", marginBottom: 8,
                      }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, fontWeight: 650, color: t.c.text }}>
                          <span style={{ width: 9, height: 9, borderRadius: 3, flexShrink: 0, background: pair ? pair.fg : t.c.primary }} />
                          <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{th.project}</span>
                          {th.minutes != null && (
                            <span style={{ marginLeft: "auto", fontSize: 11.5, fontWeight: 600, color: t.c.textSecondary, fontVariantNumeric: "tabular-nums", flexShrink: 0 }}>
                              {fmtMins(th.minutes)}{th.peopleCount ? ` · ${th.peopleCount} people` : ""}
                            </span>
                          )}
                        </div>
                        <p style={{ margin: "6px 0 0", fontSize: 12, lineHeight: 1.6, color: t.c.textSecondary }}>{th.text}</p>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 9 }}>
                          {th.projectId && (
                            <Chip t={t} onClick={() => navigate(projectPath(th.projectId, range, member, th.project))}>
                              Open project →
                            </Chip>
                          )}
                          {(th.people || []).map((p) => (
                            <Chip key={p.userId} t={t} onClick={() => navigate(personPath(p.userId, range, member, p.name))}>
                              {p.name}
                            </Chip>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}

              {data.shifts?.length > 0 && (
                <>
                  <SectionLabel t={t} delay=".38s">Shifts worth knowing</SectionLabel>
                  <div className="tgob-in" style={{ animationDelay: ".42s" }}>
                    {data.shifts.map((s, i) => {
                      const tone = TONE[s.tone] || TONE.note;
                      return (
                        <div key={i} style={{
                          display: "flex", gap: 10, alignItems: "flex-start", padding: "9px 0",
                          borderBottom: i < data.shifts.length - 1 ? `1px solid ${t.c.border}` : "none",
                          fontSize: 12.5, lineHeight: 1.55, color: t.c.textSecondary,
                        }}>
                          <span style={{
                            width: 20, height: 20, borderRadius: 6, flexShrink: 0, display: "grid",
                            placeItems: "center", fontSize: 11, marginTop: 1, background: tone.bg, color: tone.fg,
                          }}>{tone.glyph}</span>
                          <span>{s.text}</span>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {data.oneThing && (
                <>
                  <SectionLabel t={t} delay=".5s">If you do one thing</SectionLabel>
                  <p className="tgob-in" style={{ animationDelay: ".55s", fontSize: 12.5, lineHeight: 1.6, color: t.c.textSecondary, margin: 0 }}>{data.oneThing}</p>
                </>
              )}

              {data.ai === false && data.warnings?.includes("writer_unavailable") && (
                <div className="tgob-in" style={{ animationDelay: ".6s", marginTop: 16, fontSize: 11, color: t.c.textTertiary }}>
                  Written from figures alone this time — the narrative writer wasn’t available.
                </div>
              )}
            </>
          )}

          <div style={{
            marginTop: 24, paddingTop: 12, borderTop: `1px solid ${t.c.border}`,
            fontSize: 10.5, color: t.c.textTertiary,
          }}>
            Composed from your organization’s analysed activity for this window · refreshed when the range changes.
          </div>
        </div>
      </DashAntd>
    </PageShell>
  );
}

export default BriefDrilldown;
